import type { Theme as GlitzTheme } from '@glitz/core';
import { ThemeProvider as GlitzThemeProvider } from '@glitz/react';
import * as React from 'react';
import {
  a11yGrey,
  grey,
  paleBlack,
  secondaryGrey,
  shadedGrey,
  sharedFontSize,
  sharedSpacing,
  softWhite,
  tintedBlack,
  white,
} from 'webapp/shared/sharedTheme';

const secondaryLight = '#ffc166';
export const secondaryBrighter = '#ffe0b3';
const secondaryBrightest = '#fff0d9';
export const darkGrey = '#1e2022';
export const solidGrey = '#c9c0bb';

const monochrome = '#bbbcbc';
const monochromeLight = '#ebebeb';
export const monochromeBrighter = '#f5f5f5';
const creamWhite = '#fff1D9';

export const orange = '#ff9800';
export const red = '#D90005';
export const blue = '#093fff';
export const lightBlue = '#B7C7ff';
export const black = '#000';

const negative = '#f06161';

const fontSize = { xxxl: 42, xxl: 30, xl: 25, l: 20, m: 18, s: 16, xs: 14, xxs: 12, xxxs: 10 };

const fontWeight = {
  light: 300,
  normal: 400,
  bold: 500,
  primary: 500,
  secondary: 700,
};

const primary = {
  color: '#4ec791',
  hover: '#36ab76',
  text: 'white',
  border: '#4ec791',
};

const secondary = {
  color: orange,
  hover: '#cc7a00',
  text: 'white',
  border: orange,
};

const whiteTheme = {
  color: 'white',
  hover: monochromeBrighter,
  text: 'black',
  border: monochrome,
  hoverBorder: monochrome,
};

export const theme: GlitzTheme = {
  id: 'nordicnest',
  primary,
  secondary,
  tertiary: {
    color: secondaryLight,
    hover: secondaryBrighter,
    text: white,
    border: secondaryLight,
  },
  quaternary: primary,
  monochrome: {
    color: '#ebebeb',
    hover: '#eeeeee',
    text: 'black',
    border: '#ebebeb',
  },
  monochromeBrighter: {
    color: monochromeBrighter,
    hover: '#eeeeee',
    text: 'black',
    border: monochromeBrighter,
  },
  positive: {
    color: '#4ec791',
    hover: '#36ab76',
    text: 'white',
    border: '#4ec791',
  },
  negative: {
    color: negative,
    hover: '#ec3232',
    text: 'white',
    border: negative,
  },
  transparent: {
    color: 'transparent',
    hover: 'transparent',
    text: 'inherit',
    border: 'transparent',
  },
  white: whiteTheme,
  dark: {
    color: monochrome,
    hover: '#afb1b1',
    text: 'white',
    border: monochrome,
  },
  disabled: {
    color: monochromeBrighter,
    hover: monochromeBrighter,
    text: monochrome,
    border: monochromeBrighter,
  },
  myPagesMenu: {
    color: solidGrey,
    fontWeight: 500,
  },
  fontWeight,
  myPagesLoyaltyPoints: {
    color: secondaryBrightest,
    fontWeight: 500,
    textTransform: 'uppercase',
  },
  myPagesInfoBox: {
    color: monochromeLight,
  },
  myPagesProfilePage: {
    color: monochromeLight,
    textTransform: 'uppercase',
  },
  notificationColor: {
    color: blue,
    text: 'white',
  },
  myPagesButton: {
    color: 'white',
    fontWeight: 500,
  },
  myPagesText: {
    fontWeight: 500,
  },
  partialDeliveryTheme: {
    color: lightBlue,
  },
  productPageInfo: {
    color: secondaryBrighter,
  },
  fontFamily: {
    primary: 'InterVariable',
    secondary: 'haptik, InterVariable',
    tertiary: 'haptik, InterVariable',
  },
  footer: {
    color: secondaryBrighter,
    heading: {
      fontSize: fontSize.s,
      fontWeight: fontWeight.bold,
    },
    infoFooter: {
      color: darkGrey,
      text: monochrome,
    },
    accordion: {
      backgroundColor: white,
      borderColor: monochromeLight,
    },
  },
  memberClubNewsletter: {
    color: secondaryBrightest,
    fontSize: fontSize.xs,
  },
  quickSearch: {
    borderColor: shadedGrey,
    backgroundColor: softWhite,
    borderRadius: 50,
    highlightText: black,
  },
  searchSuggestions: {
    borderRadius: 16,
    position: 40,
    borderColor: secondaryGrey,
    color: a11yGrey,
    backgroundColor: white,
  },
  price: {
    current: {
      card: { fontWeight: fontWeight.normal, fontSize: fontSize.xxs },
      page: { fontSize: fontSize.m },
    },
    original: {
      fontSize: fontSize.xxs,
      fontWeight: fontWeight.normal,
    },
    discount: {
      color: red,
    },
    vatText: {
      fontWeight: fontWeight.bold,
    },
  },
  productCard: {
    title: { fontSize: fontSize.xs, color: black },
    brand: { fontSize: fontSize.xs, fontWeight: 600 },
    price: { fontSize: fontSize.xs },
    wishlistIcon: { color: black },
  },
  topBanner: {
    color: softWhite,
    text: 'inherit',
    checkColor: orange,
  },
  button: { textTransform: 'initial' },
  header: {
    backgroundColor: white,
    fontSize: fontSize.xxs,
    fontWeight: fontWeight.bold,
    menuTextTransform: 'uppercase',
    subCategoryFontSize: fontSize.xxs,
    menuHeadingFontSize: fontSize.l,
    menuHightlightColor: monochromeLight,
    topMenu: { fontWeight: fontWeight.bold },
  },
  heading: {
    fontWeight: fontWeight.normal,
    textTransform: 'uppercase',
  },
  headerIndicators: {
    cart: {
      backgroundColor: primary.color,
      color: 'white',
    },
    favourites: {
      backgroundColor: orange,
      color: 'white',
    },
  },
  carouselButton: {
    color: softWhite,
    hover: grey,
    text: darkGrey,
    border: softWhite,
  },
  secondaryCarouselButton: {
    color: white,
    hover: softWhite,
    text: darkGrey,
    border: white,
  },
  actionButton: primary,
  filterButton: secondary,
  linkButton: secondary,
  wishlistButton: whiteTheme,
  bubble: { color: 'inherit', backgroundColor: secondaryLight },
  checkbox: {
    color: 'white',
    uncheckedColor: 'transparent',
    checkedColor: 'white',
    disabled: monochrome,
    borderColor: tintedBlack,
    checkedBackgroundColor: tintedBlack,
  },
  sharedSpacing: sharedSpacing,
  spacing: { xxxxl: 70, xxxl: 70, xxl: 70, xl: 40, l: 24, m: 16, s: 12, xs: 8, xxs: 4 },
  sharedFontSize: sharedFontSize,
  fontSize: { xxxl: 70, xxl: 42, xl: 30, l: 20, m: 18, s: 16, xs: 14, xxs: 12, xxxs: 10 },
  radius: {
    primary: 0,
    secondary: 0,
  },
  monochromeLight,
  darkGrey,
  secondaryBrighter,
  secondaryBrightest,
  secondaryLight,
  creamWhite,
  lightBackground: 'white',
  flyout: {
    headerColor: white,
    headerHeight: 100,
    headerTextPosition: 'start',
    backgroundColor: white,
  },
  ratings: { backgroundColor: shadedGrey, color: secondaryLight },
  borderColor: monochromeLight,
  borderHighlight: secondaryLight,
  breadcrumbs: {
    fontSizeLargeViewport: fontSize.xxs,
    fontSizeBase: fontSize.xxs,
    segmentFontWeight: 550,
    lastSegmentColor: `${darkGrey}7F`,
    color: darkGrey,
  },
  borderStrongColor: solidGrey,
  stockStatus: {
    inStock: primary.color,
    lowInStock: secondary.color,
    outOfStock: negative,
    unknown: monochrome,
  },
  newsBadge: {
    color: black,
    text: white,
  },
  thumbnailBorder: monochromeLight,
  mobileMenu: {
    backgroundColor: white,
    accentColor: monochromeLight,
    borderColor: monochromeBrighter,
    fontSize: fontSize.s,
    fontWeight: fontWeight.bold,
    tabs: {
      fontSize: fontSize.xs,
      fontWeight: fontWeight.bold,
      textTransform: 'initial',
    },
  },
  checkout: {
    backgroundColor: monochromeBrighter,
    text: 'inherit',
  },
  facets: {
    flyoutHeader: { fontSize: fontSize.l, color: white },
    heading: { fontSize: fontSize.s, fontWeight: fontWeight.bold },
    facet: { fontSize: fontSize.xxs },
    count: { fontSize: fontSize.xxs, color: monochrome },
    borderColor: softWhite,
    search: {
      color: white,
      borderColor: monochrome,
    },
    range: {
      sliderTrackColor: secondaryBrighter,
      sliderMarkColor: secondary.color,
      sliderRailColor: softWhite,
      markDiameter: 16,
      railHeight: 4,
    },
    adornment: {
      input: { color: monochrome },
      affix: { fontSize: sharedFontSize.xs, color: monochrome },
    },
  },
  scrollbar: {
    trackColor: monochromeLight,
    thumbColor: monochrome,
    radius: 10,
  },
  imageBackgroundColor: 'none',
  focalPoint: {
    iconBackgroundColor: monochromeBrighter,
    imageBackgroundColor: white,
  },
  outline: paleBlack,
  inputFocusOutlineColor: orange,
  materialSamples: {
    titleFontWeight: fontWeight.normal,
    descriptionFontWeight: fontWeight.light,
    stockStatusFontWeight: fontWeight.normal,
  },
};

export function ThemeProvider(props: React.PropsWithChildren<{}>) {
  return <GlitzThemeProvider theme={theme}>{props.children}</GlitzThemeProvider>;
}

// Used for `@glitz/static`
export const staticThemes = [theme];
